'use client';
import React, { forwardRef, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { BarChart3, ChevronDown, ClipboardList, LogOut, Menu, Settings, X } from 'lucide-react';
import { formatoReloj, etiquetaRol, textosConfirmarHub, medirEncabezado, ZONA_CR } from './piezasEncabezado.js';
import ConfirmDialog from './ConfirmDialog';

export { agruparSecciones, revisarAgrupado, GRUPOS, ORDEN_GRUPOS, MAX_ENTRADAS } from './agruparSecciones.js';
export {
  formatoReloj, etiquetaRol, ETIQUETAS_ROL, ZONA_CR, textosConfirmarHub, TEXTOS_CONFIRMAR_HUB,
  modoEncabezado, necesarioFila, medirEncabezado, rellenoFila, RELLENO_FILA,
} from './piezasEncabezado.js';

// AppHeaderBase: el encabezado común SIN react-router (2026-10-20). Es todo el
// componente; AppHeader.jsx solo lo envuelve con el Link y la ruta de
// react-router. Las apps que no usan react-router (el admin de encuestas es
// Next.js) importan este archivo y pasan el enlace y la ruta por props:
//
//   'use client';
//   import Link from 'next/link';
//   import { usePathname } from 'next/navigation';
//   import AppHeaderBase, { agruparSecciones } from '@/components/ui/AppHeaderBase';
//   <AppHeaderBase titulo="Encuestas" componenteEnlace={Link} rutaActual={usePathname()} secciones={...} />
//
// Props propias de la base (además de las de AppHeader, abajo):
//   componenteEnlace  componente de enlace interno; recibe `href` (como
//                     next/link o un <a>), className, onClick, role, title y
//                     aria-current. Por defecto un <a href> común.
//   rutaActual        ruta para marcar la sección activa. Por defecto
//                     window.location.pathname (o '' sin window).
//
// Lleva 'use client' para Next (usa estado y efectos). Vite ignora la directiva.
// Depende de './agruparSecciones.js', './piezasEncabezado.js' y
// './ConfirmDialog' (sync-ui.sh --only=header copia los cinco juntos).
//
// ─── Pantallas móviles y kioscos: confirmarHub (2026-10-20) ────────────────
// En los equipos compartidos del personal (tablet con PIN, cuenta kiosco) el
// logo pide confirmación antes de salir al hub, con el ConfirmDialog común:
//   <AppHeaderBase titulo="Marcas" confirmarHub="Va a salir de la tablet de
//     marcas. Para volver, abra marcas.vlscr.net" ... />
//   confirmarHub  string (el mensaje) u objeto { titulo, mensaje, confirmar,
//                 cancelar }; true usa los textos por defecto ('¿Volver al
//                 hub?', 'Ir al hub', 'Quedarme'). Sin él el logo navega
//                 directo. Escape, Cancelar o tocar fuera se quedan en la app;
//                 el foco vuelve al logo.
//   alIrAlHub     función(url) que navega al confirmar; por defecto
//                 window.location.assign(url) (para pruebas y capturas).
// El criterio completo (quién lleva confirmación y Salir) está en README.md,
// sección 'Pantallas móviles y kioscos'.
//
// ─── Piezas comunes de `acciones` (2026-10-20) ──────────────────────────────
//   import AppHeader, { RelojEncabezado, ChipRol } from '../components/ui/AppHeader';
//   acciones={<><RelojEncabezado /><ChipRol rol={role} /></>}
// Orden en la derecha: accionesMenu, acciones propias de la app (botones),
// reloj, rol, iniciales y Salir. Nada de relojes ni chips propios
// (check-estilo avisa `accion-propia`).
//   RelojEncabezado  24 h, hora de Costa Rica: 'mar 20 oct · 17:24' desde sm y
//                    solo '17:24' en el teléfono, misma caja de 36 px y números
//                    tabulares. Props: ahora (Date fija, para pruebas y vistas
//                    simuladas), zona ('America/Costa_Rica'), intervalo (ms,
//                    15000), className.
//   ChipRol          un solo chip: Administrador / Editor / Lectura / Kiosco
//                    (etiquetaRol traduce admin, viewer, kiosk...). Props: rol
//                    (o role), etiqueta (texto fijo en vez del del rol),
//                    enTelefono (false: se oculta bajo sm), className.
//
// ─── Regla de 390 (teléfono, < 640 px) ─────────────────────────────────────
// El título se ve completo hasta ~22 caracteres a 390 px:
//   - sin ceja ni separador; logo de 32 px de alto (40 px desde sm);
//   - título de 15 px en hasta 2 líneas (desde sm, 16 px en una línea);
//   - si hay menú compacto, Salir pasa al menú (la fila queda con las
//     acciones y el botón de menú);
//   - en `acciones` caben el reloj (corto) y a lo sumo UN botón de 44 px; el
//     resto va en `accionesMenu`, que en el teléfono sale dentro del menú.
// Con reloj y menú le quedan al título ~168 px: 2 líneas de ~19 caracteres.
//
// ─── A todo el ancho (2026-10-20) ──────────────────────────────────────────
// Pedido de Isaac: "necesito que el appheader sea de toda pantalla para que se
// ocupe mejor el espacio y que sea consistente". La fila ya no lleva max-w ni
// mx-auto: el logo queda pegado a la izquierda y las acciones a la derecha, con
// relleno lateral fijo de 16 px en el teléfono, 24 px desde sm y 32 px desde lg
// (px literales: varias apps redefinen la escala de Tailwind). Las secciones
// siguen justo después del nombre, no centradas.
// El menú compacto entra por el ancho REAL de la fila (medirEncabezado en
// piezasEncabezado.js, con ResizeObserver): desde 640 px las secciones van en
// la fila si caben enteras con el título entero y la derecha; si no, al menú.
// Bajo 640 px siempre menú (regla de 390). Sin medida (SSR, jsdom) manda
// `navDesde`. El atributo data-modo de la fila dice qué decidió ('fila',
// 'compacto' o 'corte' mientras no hay medida).
//
// Estado activo, uno solo (2026-10-20): sección, grupo, ítem del desplegable y
// del menú compacto activos van con bg-primary/10 text-primary y el ícono en
// trazo 2.5. Sin subrayado ni barra inferior (check-estilo avisa
// `activo-distinto` en las apps que pintan la suya).
//
// ─── Lo que sigue es la documentación de siempre del AppHeader ────────────
//
// Encabezado común de las apps del hub (armonía visual Fase 2, decisión de
// Isaac 2026-10-15). Generaliza el encabezado de fincas
// (bitacora-fincas/src/components/ui/NavigationHeader.jsx), que cables y la
// General de alineaciones ya copiaron: logo que lleva al hub, separador,
// nombre del módulo en dos niveles y el menú de secciones EN LA MISMA FILA,
// separado del nombre (no en segunda fila ni pegado a la derecha). A la
// derecha, las acciones propias de la app (reloj, rol), el usuario y Salir.
//
// Medidas (contrato de la Fase 2, ancho del 2026-10-20): fila de 64 px a todo
// el ancho con relleno de 16 / 24 (sm) / 32 px (lg), logo de 40 px de alto,
// título font-display de 16 px, ceja en Source Sans 3 de 12 px (fijada, no
// heredada del body: en canopy, planilla y marcas salía en Inter, 118 px en vez
// de 99), Salir y menú de 44 px (táctil mínimo). Holgura (2026-10-20): logo,
// separador y nombre a 8 px, secciones a 16 px del nombre, la derecha sin
// relleno propio (el gap de 8 px la separa) e iniciales sin margen, y el reloj
// común sin ícono y en Source Sans 3. Todas las medidas van en píxeles
// fijos (h-[64px], px-[16px]...), no con la escala de Tailwind: varias apps
// del hub redefinen el spacing al doble (1 = 8 px, 4 = 32 px) y un px-4 sale
// a 32 px en unas y a 16 px en otras (mismo motivo que en Modal.jsx).
//
// ─── Migración (mecánica) ────────────────────────────────────────────────
//   ANTES: <NavigationHeader /> o <header>…</header> propio de la app.
//   DESPUÉS:
//     import { Home, CreditCard } from 'lucide-react';
//     <AppHeader
//       titulo="Préstamos"
//       secciones={[
//         { label: 'Consolidado', to: '/', icon: Home, exact: true },
//         { label: 'Préstamos', to: '/prestamos', icon: CreditCard },
//         // Grupo con menú desplegable (como fincas):
//         { label: 'Pagos', icon: Wallet, items: [{ label: 'Tarifas', to: '/tarifas', icon: Hash }] },
//       ]}
//       acciones={<><RelojEncabezado /><ChipRol rol={role} /></>}
//       usuario={nombre}
//       onSalir={signOut}
//     />
//   El encabezado es `sticky top-0`: ocupa su lugar en el flujo, así que el
//   <main> ya no necesita padding-top de 64 px (si el header viejo era
//   `fixed`, quitar ese relleno).
//
// Props:
//   titulo      nombre del módulo (obligatorio).
//   eyebrow     línea chica sobre el título (por defecto "Vista Los Sueños";
//               null la quita). Se ve desde sm.
//   secciones   [{ label, to, icon?, exact?, activa?, title? }] o grupos
//               [{ label, icon?, items: [...] }]. `icon` es un componente
//               (lucide), un nodo ya armado o el nombre de un ícono de grupo
//               ('ClipboardList' | 'Settings' | 'BarChart3'). `activa`
//               (boolean) manda sobre el cálculo por ruta; sin ella, activa =
//               ruta exacta (`exact`) o la ruta y sus hijas. `to` con http(s)
//               va como <a href>.
//               Convención (2026-10-20): máximo 4 entradas en la fila contando
//               grupos; armar los grupos Operación / Reportes / Gestión con
//               agruparSecciones(secciones, { sueltas }) (ver README.md).
//   navDesde    'lg' (por defecto) | 'md': punto de corte de respaldo,
//               mientras no hay medida (SSR, pruebas en jsdom, navegador sin
//               ResizeObserver). Con medida decide el ancho real (ver arriba).
//               Bajo él (o si no caben) las secciones pasan al menú compacto
//               (botón de 44 px y panel bajo el encabezado, sin cambiar los 64 px).
//   ancho       obsoleto (2026-10-20): el encabezado va siempre a todo el ancho;
//               'contenido' y 'completo' se aceptan y no cambian nada.
//   acciones    nodo a la derecha, antes del usuario (botones, RelojEncabezado,
//               ChipRol). Se ve también en el teléfono.
//   accionesMenu nodo con acciones secundarias: en la fila desde sm (antes de
//               `acciones`) y, en el teléfono, dentro del menú compacto (si no
//               hay secciones, el botón de menú aparece solo en el teléfono).
//               Se pinta dos veces (fila y menú abierto): sin ids fijos.
//   usuario     nombre de quien inició sesión: iniciales desde `navDesde` y
//               el nombre completo arriba del menú compacto.
//   onSalir     cierra la sesión; sin él no se muestra el botón Salir.
//   hubUrl      destino del logo (por defecto https://vlscr.net).
//   confirmarHub texto del diálogo que confirma antes de ir al hub (equipos
//               compartidos; ver arriba).
//   logoSrc     ruta del logo (por defecto /vls-logo.png: el logo oficial,
//               shared/vls-ui/logo/vls-logo.png, que sync-ui.sh --only=logo
//               copia a public/ de cada app).
//   logoAlt     texto alternativo del logo.
//
// Colores con las clases de tokens del hub (bg-card, border-border,
// bg-primary/10...), que toda app con vls-tailwind-colors.cjs genera.
// Depende de lucide-react (AppHeader.jsx, además, de react-router-dom).

const HUB_URL = 'https://vlscr.net';

// La fila a todo el ancho, relleno lateral fijo de 16 / 24 / 32 px. El panel
// del menú compacto usa el mismo relleno para que sus ítems queden alineados
// con el logo.
const CLASE_FILA = 'relative w-full h-[64px] px-[16px] sm:px-[24px] lg:px-[32px] flex items-center gap-[8px]';
const RELLENO_PANEL = 'py-[16px] px-[16px] sm:px-[24px] lg:px-[32px]';

// Clases estáticas por punto de corte (Tailwind solo genera las que lee
// enteras en el código). Sin medida mandan estas.
const CORTES = {
  md: { fila: 'hidden md:flex', compacto: 'md:hidden', iniciales: 'hidden md:inline-flex' },
  lg: { fila: 'hidden lg:flex', compacto: 'lg:hidden', iniciales: 'hidden lg:inline-flex' },
};
// Con medida (ResizeObserver) manda el modo. En 'compacto' el <nav> sigue en el
// DOM, sin ancho, invisible y fuera del flujo: así se puede volver a medir su
// ancho natural (scrollWidth) cuando cambia la ventana, sin desbordar la página.
const MODOS = {
  fila: { fila: 'flex', compacto: 'hidden', iniciales: 'inline-flex' },
  compacto: {
    fila: 'flex absolute w-0 overflow-hidden invisible pointer-events-none',
    compacto: '',
    iniciales: 'hidden',
  },
};

// useLayoutEffect en el navegador (mide antes de pintar, sin parpadeo) y
// useEffect en el servidor (Next.js avisa si se usa useLayoutEffect ahí).
const useLayoutEffectSeguro = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const esGrupo = (s) => Array.isArray(s?.items);
const esExterno = (to) => typeof to === 'string' && /^https?:\/\//.test(to);

export function seccionActiva(item, pathname) {
  if (typeof item?.activa === 'boolean') return item.activa;
  if (!item?.to || esExterno(item.to)) return false;
  if (item.exact) return pathname === item.to;
  if (item.to === '/') return pathname === '/';
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

export function iniciales(nombre) {
  const partes = String(nombre || '').trim().split(/\s+/).filter(Boolean);
  return ((partes[0]?.[0] || '') + (partes[1]?.[0] || '')).toUpperCase() || '?';
}

// Íconos de grupo por nombre (agruparSecciones los devuelve así).
export const ICONOS_POR_NOMBRE = { ClipboardList, Settings, BarChart3 };

function Icono({ icon, size = 16, activo }) {
  if (!icon) return null;
  if (React.isValidElement(icon)) return icon;
  const Componente = typeof icon === 'string' ? ICONOS_POR_NOMBRE[icon] : icon;
  if (!Componente) return null;
  return <Componente size={size} strokeWidth={activo ? 2.5 : 2} aria-hidden="true" className="flex-shrink-0" />;
}

// Enlace interno por defecto: un <a href> común (sin router).
export const EnlaceHtml = forwardRef(function EnlaceHtml({ href, children, ...props }, ref) {
  return <a ref={ref} href={href} {...props}>{children}</a>;
});

function Enlace({ item, className, onClick, role, activo, componente: Componente = EnlaceHtml }) {
  const comun = {
    className,
    onClick,
    role,
    title: item.title,
    'aria-current': activo ? 'page' : undefined,
  };
  if (esExterno(item.to)) {
    return <a href={item.to} {...comun}>{item.contenido}</a>;
  }
  return <Componente href={item.to} {...comun}>{item.contenido}</Componente>;
}

const rutaDelNavegador = () => (typeof window !== 'undefined' && window.location ? window.location.pathname : '');
const irAlHubPorDefecto = (url) => { if (typeof window !== 'undefined') window.location.assign(url); };

// enMenu: en el menú desplegable cada ítem es 'flex' y ocupa su propia fila
// (con 'inline-flex', que le gana a 'flex', quedaban 2 ítems por fila).
const claseFila = (activo, enMenu = false) => [
  enMenu ? 'flex' : 'inline-flex',
  'items-center gap-[6px] h-[44px] px-[8px] rounded-md font-caption font-medium text-sm whitespace-nowrap select-none transition-colors',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  activo ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted',
].join(' ');

// Menú desplegable de un grupo en la fila (igual que fincas): botón con
// aria-haspopup/aria-expanded, flechas mueven el foco, Escape devuelve el
// foco al botón y Tab cierra.
function GrupoDesplegable({ grupo, pathname, abierto, onToggle, onClose, componenteEnlace }) {
  const panelRef = useRef(null);
  const botonRef = useRef(null);
  const activo = grupo.items.some((i) => seccionActiva(i, pathname));

  const enfocar = useCallback((indice) => {
    const items = panelRef.current?.querySelectorAll('[role="menuitem"]');
    if (!items || items.length === 0) return;
    items[((indice % items.length) + items.length) % items.length]?.focus();
  }, []);

  const teclaBoton = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle(true);
      setTimeout(() => enfocar(0), 0);
    }
  };

  const teclaPanel = (e) => {
    const items = Array.from(panelRef.current?.querySelectorAll('[role="menuitem"]') || []);
    const actual = items.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); enfocar(actual + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); enfocar(actual - 1); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); botonRef.current?.focus(); }
    else if (e.key === 'Tab') onClose();
  };

  return (
    <div className="relative flex-shrink-0">
      <button
        ref={botonRef}
        type="button"
        onClick={() => onToggle(!abierto)}
        onKeyDown={teclaBoton}
        aria-haspopup="menu"
        aria-expanded={abierto}
        className={claseFila(activo)}
      >
        <Icono icon={grupo.icon} activo={activo} />
        <span>{grupo.label}</span>
        <ChevronDown size={14} aria-hidden="true" className={`transition-transform ${abierto ? 'rotate-180' : ''}`} />
      </button>
      {abierto && (
        <div
          ref={panelRef}
          role="menu"
          aria-label={grupo.label}
          onKeyDown={teclaPanel}
          className="absolute left-0 top-full mt-[4px] min-w-[220px] bg-card border border-border rounded-md shadow-md py-[4px] z-50"
        >
          {grupo.items.map((item) => {
            const a = seccionActiva(item, pathname);
            return (
              <Enlace
                key={item.to}
                componente={componenteEnlace}
                role="menuitem"
                activo={a}
                onClick={onClose}
                className={`mx-[4px] ${claseFila(a, true)}`}
                item={{ ...item, contenido: <><Icono icon={item.icon} activo={a} /><span>{item.label}</span></> }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function ItemCompacto({ item, pathname, onClick, componenteEnlace }) {
  const a = seccionActiva(item, pathname);
  return (
    <Enlace
      componente={componenteEnlace}
      activo={a}
      onClick={onClick}
      className={[
        'flex items-center gap-[16px] min-h-[48px] px-[16px] py-[12px] rounded-xl font-caption font-medium text-base transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        // El mismo activo que la fila (un solo estilo, 2026-10-20).
        a ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted',
      ].join(' ')}
      item={{ ...item, contenido: <><Icono icon={item.icon} size={20} activo={a} /><span>{item.label}</span></> }}
    />
  );
}

// Ceja, reloj y chip van con la fuente del contrato fija: heredarla del body
// dejaba la ceja en Inter en las apps que todavía tienen Inter en el body
// (canopy, planilla, marcas: 118 px en vez de 99). Source Sans 3 además es
// más angosta que Inter: reloj y chip ocupan ~35 px menos en la fila.
const FUENTE_SANS = { fontFamily: "'Source Sans 3', system-ui, sans-serif" };

// Reloj común del encabezado (ver arriba). Se actualiza cada `intervalo` ms;
// con `ahora` queda fijo (pruebas, capturas con datos simulados).
export function RelojEncabezado({ ahora, zona = ZONA_CR, intervalo = 15000, className = '' }) {
  const fija = ahora instanceof Date || typeof ahora === 'number' || typeof ahora === 'string' ? new Date(ahora) : null;
  const fijaMs = fija ? fija.getTime() : null;
  const [fecha, setFecha] = useState(() => fija || new Date());
  useEffect(() => {
    if (fijaMs !== null) {
      setFecha(new Date(fijaMs));
      return undefined;
    }
    setFecha(new Date());
    const id = setInterval(() => setFecha(new Date()), intervalo);
    return () => clearInterval(id);
  }, [fijaMs, intervalo]);
  const r = formatoReloj(fecha, zona);
  return (
    <time
      dateTime={r.iso}
      title="Hora de Costa Rica"
      suppressHydrationWarning
      className={`inline-flex items-center gap-[6px] h-[36px] px-[8px] rounded-md border border-border bg-card text-muted-foreground text-[13px] leading-[16px] tabular-nums whitespace-nowrap flex-shrink-0 ${className}`.trim()}
      style={FUENTE_SANS}
    >
      <span className="hidden sm:inline" suppressHydrationWarning>{r.fecha}</span>
      <span className="hidden sm:inline" aria-hidden="true">·</span>
      <span className="font-medium text-foreground" suppressHydrationWarning>{r.hora}</span>
    </time>
  );
}

// Chip común del rol de la sesión (ver arriba).
export function ChipRol({ rol, role, etiqueta, enTelefono = false, className = '' }) {
  const texto = etiqueta || etiquetaRol(rol ?? role);
  if (!texto) return null;
  return (
    <span
      title={`Rol: ${texto}`}
      className={`${enTelefono ? 'inline-flex' : 'hidden sm:inline-flex'} items-center h-[24px] px-[8px] rounded-full bg-muted text-muted-foreground font-medium text-[12px] leading-[16px] whitespace-nowrap flex-shrink-0 ${className}`.trim()}
      style={FUENTE_SANS}
    >
      {texto}
    </span>
  );
}

export default function AppHeaderBase({
  titulo,
  eyebrow = 'Vista Los Sueños',
  secciones = [],
  navDesde = 'lg',
  acciones = null,
  accionesMenu = null,
  usuario = '',
  onSalir,
  hubUrl = HUB_URL,
  logoSrc = '/vls-logo.png',
  logoAlt = 'Vista Los Sueños Adventure Park',
  componenteEnlace = EnlaceHtml,
  rutaActual,
  confirmarHub,
  alIrAlHub = irAlHubPorDefecto,
}) {
  const pathname = (rutaActual ?? rutaDelNavegador()) || '';
  const textosHub = textosConfirmarHub(confirmarHub);
  const [confirmandoHub, setConfirmandoHub] = useState(false);
  const [yendoAlHub, setYendoAlHub] = useState(false);
  const [compactoAbierto, setCompactoAbierto] = useState(false);
  const [grupoAbierto, setGrupoAbierto] = useState(null);
  const headerRef = useRef(null);
  const toggleRef = useRef(null);
  const filaRef = useRef(null);
  const marcaRef = useRef(null);
  const nombreRef = useRef(null);
  const tituloRef = useRef(null);
  const cejaRef = useRef(null);
  const navRef = useRef(null);
  const derechaRef = useRef(null);
  const inicialesRef = useRef(null);
  const panelId = useId();
  const compactoRef = useRef(false);
  compactoRef.current = compactoAbierto;
  const haySecciones = secciones.length > 0;
  // Modo medido por el ancho real ('fila' | 'compacto'); null = sin medida, manda navDesde.
  const [modo, setModo] = useState(null);
  const corte = (haySecciones && MODOS[modo]) || CORTES[navDesde] || CORTES.lg;
  // Menú compacto: con secciones, cuando no caben (o bajo navDesde sin medida);
  // solo con accionesMenu, en el teléfono.
  const hayMenu = haySecciones || !!accionesMenu;
  const corteMenu = haySecciones ? corte.compacto : 'sm:hidden';

  // Medir con el ancho real: al montar, al cambiar el tamaño de la fila, de la
  // marca o de la derecha, al cargar las fuentes y cuando cambian las secciones.
  const firmaSecciones = secciones.map((s) => (esGrupo(s) ? `${s.label}[${s.items.length}]` : s.label)).join('|');
  useLayoutEffectSeguro(() => {
    if (!haySecciones) { setModo(null); return undefined; }
    const fila = filaRef.current;
    if (!fila || typeof window === 'undefined' || typeof window.ResizeObserver !== 'function') return undefined;
    let vivo = true;
    const medir = () => {
      if (!vivo) return;
      const m = medirEncabezado({
        fila,
        marca: marcaRef.current,
        nombreCaja: nombreRef.current,
        titulo: tituloRef.current,
        ceja: cejaRef.current,
        secciones: navRef.current,
        derecha: derechaRef.current,
        toggle: toggleRef.current,
        iniciales: inicialesRef.current,
        usuario: !!usuario,
      });
      if (m) setModo(m.modo);
    };
    medir();
    const observador = new window.ResizeObserver(() => medir());
    [fila, marcaRef.current, derechaRef.current, navRef.current].forEach((el) => el && observador.observe(el));
    document.fonts?.ready?.then(medir).catch(() => {});
    return () => { vivo = false; observador.disconnect(); };
  }, [haySecciones, firmaSecciones, titulo, eyebrow, !!usuario]);

  // Al pasar a la fila se cierra el menú compacto; al pasar al menú, el desplegable.
  useEffect(() => {
    if (modo === 'fila') setCompactoAbierto(false);
    if (modo === 'compacto') setGrupoAbierto(null);
  }, [modo]);

  // Clic fuera y Escape cierran el menú compacto y el desplegable abierto.
  useEffect(() => {
    const clicFuera = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setCompactoAbierto(false);
        setGrupoAbierto(null);
      }
    };
    const escape = (e) => {
      if (e.key !== 'Escape') return;
      setGrupoAbierto(null);
      if (compactoRef.current) {
        setCompactoAbierto(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', clicFuera);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', clicFuera);
      document.removeEventListener('keydown', escape);
    };
  }, []);

  // Volver con Atrás desde el hub (página restaurada de la caché del
  // navegador): el diálogo no queda abierto en 'Espere…'.
  useEffect(() => {
    if (!textosHub || typeof window === 'undefined') return undefined;
    const alVolver = (e) => { if (e.persisted) { setYendoAlHub(false); setConfirmandoHub(false); } };
    window.addEventListener('pageshow', alVolver);
    return () => window.removeEventListener('pageshow', alVolver);
  }, [!!textosHub]);

  // Al cambiar de ruta se cierra todo.
  useEffect(() => {
    setCompactoAbierto(false);
    setGrupoAbierto(null);
  }, [pathname]);

  const cerrarCompacto = () => setCompactoAbierto(false);
  const ContenedorMenu = haySecciones ? 'nav' : 'div';

  // Logo con confirmarHub: en vez de navegar abre el diálogo común.
  const clicLogo = (e) => {
    if (!textosHub) return;
    e.preventDefault();
    setCompactoAbierto(false);
    setGrupoAbierto(null);
    setConfirmandoHub(true);
  };
  const irAlHub = () => {
    setYendoAlHub(true);
    alIrAlHub(hubUrl);
  };

  return (
    <>
    <header
      ref={headerRef}
      className="sticky top-0 z-40 bg-card border-b border-border shadow-sm"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <div ref={filaRef} data-modo={haySecciones && modo ? modo : 'corte'} className={CLASE_FILA}>
        {/* Marca: logo al hub + separador + nombre del módulo, pegada a la izquierda */}
        <div ref={marcaRef} className="flex items-center gap-[8px] min-w-0">
          <a
            href={hubUrl}
            onClick={clicLogo}
            aria-haspopup={textosHub ? 'dialog' : undefined}
            title="Ir al hub Vista Los Sueños"
            className="inline-flex items-center min-h-[44px] flex-shrink-0 rounded-md transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <img src={logoSrc} alt={logoAlt} className="h-[32px] sm:h-[40px] w-auto object-contain" />
          </a>
          <span className="hidden sm:block h-[28px] w-px bg-border flex-shrink-0" aria-hidden="true" />
          <div ref={nombreRef} className="flex flex-col min-w-0">
            {eyebrow && (
              <span
                ref={cejaRef}
                className="hidden sm:block text-[12px] leading-[16px] font-normal uppercase tracking-[0.025em] text-muted-foreground whitespace-nowrap"
                style={FUENTE_SANS}
              >
                {eyebrow}
              </span>
            )}
            <span
              ref={tituloRef}
              title={titulo}
              className="font-display font-semibold text-[15px] leading-[18px] sm:text-[16px] sm:leading-[24px] text-foreground line-clamp-2 sm:line-clamp-1 [overflow-wrap:anywhere]"
            >
              {titulo}
            </span>
          </div>
        </div>

        {/* Secciones en la misma fila, justo después del nombre (16 px), no
            centradas. No se encogen: si no caben enteras pasan al menú compacto. */}
        {haySecciones && (
          <nav ref={navRef} aria-label="Secciones" className={`${corte.fila} items-center gap-[4px] ml-[16px] flex-shrink-0`}>
            {secciones.map((s) => (esGrupo(s) ? (
              <GrupoDesplegable
                key={s.label}
                grupo={s}
                pathname={pathname}
                componenteEnlace={componenteEnlace}
                abierto={grupoAbierto === s.label}
                onToggle={(abrir) => setGrupoAbierto(abrir ? s.label : null)}
                onClose={() => setGrupoAbierto(null)}
              />
            ) : (
              <Enlace
                key={s.to}
                componente={componenteEnlace}
                activo={seccionActiva(s, pathname)}
                className={`flex-shrink-0 ${claseFila(seccionActiva(s, pathname))}`}
                item={{ ...s, contenido: <><Icono icon={s.icon} activo={seccionActiva(s, pathname)} /><span>{s.label}</span></> }}
              />
            )))}
          </nav>
        )}

        {/* Derecha, pegada al borde: acciones de la app, usuario, Salir y menú compacto */}
        <div ref={derechaRef} className="ml-auto flex items-center gap-[4px] flex-shrink-0">
          {accionesMenu && <div className="hidden sm:flex items-center gap-[4px]">{accionesMenu}</div>}
          {acciones}
          {usuario && (
            <span
              ref={inicialesRef}
              role="img"
              aria-label={`Sesión de ${usuario}`}
              title={usuario}
              className={`${corte.iniciales} items-center justify-center w-[36px] h-[36px] rounded-full bg-primary/10 text-primary text-xs font-semibold`}
            >
              {iniciales(usuario)}
            </span>
          )}
          {onSalir && (
            <button
              type="button"
              onClick={() => onSalir()}
              title="Cerrar sesión"
              aria-label="Salir"
              className={`${hayMenu ? 'hidden sm:inline-flex' : 'inline-flex'} items-center justify-center w-[44px] h-[44px] rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
            >
              <LogOut size={18} aria-hidden="true" />
            </button>
          )}
          {hayMenu && (
            <button
              ref={toggleRef}
              type="button"
              onClick={() => setCompactoAbierto((v) => !v)}
              aria-expanded={compactoAbierto}
              aria-controls={panelId}
              aria-label={compactoAbierto ? 'Cerrar menú' : 'Abrir menú'}
              className={`${corteMenu} inline-flex items-center justify-center w-[44px] h-[44px] rounded-md text-foreground hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
            >
              {compactoAbierto ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
            </button>
          )}
        </div>
      </div>

      {/* Menú compacto: panel bajo la fila, la fila sigue midiendo 64 px. En el
          teléfono trae también accionesMenu y Salir (regla de 390). */}
      {hayMenu && compactoAbierto && (
        <div
          id={panelId}
          className={`${corteMenu} absolute left-0 right-0 top-full bg-card border-t border-border shadow-lg overflow-y-auto`}
          style={{ maxHeight: 'calc(100dvh - 64px - env(safe-area-inset-top, 0px))' }}
        >
          <ContenedorMenu
            aria-label={haySecciones ? 'Secciones (menú)' : undefined}
            className={`flex flex-col gap-[4px] ${RELLENO_PANEL} pb-[max(env(safe-area-inset-bottom,0px),16px)]`}
          >
            {usuario && (
              <p className="px-[16px] pb-[8px] text-sm text-muted-foreground truncate">{usuario}</p>
            )}
            {accionesMenu && (
              <div className="sm:hidden flex flex-wrap items-center gap-[8px] px-[16px] pb-[8px]">{accionesMenu}</div>
            )}
            {secciones.map((s) => (esGrupo(s) ? (
              <div key={s.label} className="flex flex-col gap-[4px] mt-[8px] first:mt-0">
                <h2 className="px-[16px] pb-[4px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">{s.label}</h2>
                {s.items.map((item) => (
                  <ItemCompacto key={item.to} item={item} pathname={pathname} componenteEnlace={componenteEnlace} onClick={cerrarCompacto} />
                ))}
              </div>
            ) : (
              <ItemCompacto key={s.to} item={s} pathname={pathname} componenteEnlace={componenteEnlace} onClick={cerrarCompacto} />
            )))}
            {onSalir && (
              <div className="sm:hidden mt-[8px] pt-[8px] border-t border-border">
                <button
                  type="button"
                  onClick={() => { cerrarCompacto(); onSalir(); }}
                  className="w-full flex items-center gap-[16px] min-h-[48px] px-[16px] py-[12px] rounded-xl font-caption font-medium text-base text-foreground hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <LogOut size={20} aria-hidden="true" />
                  <span>Cerrar sesión</span>
                </button>
              </div>
            )}
          </ContenedorMenu>
        </div>
      )}
    </header>
    {/* Fuera del <header>: el sticky z-40 no lo deja debajo de nada, y z-[60]
        lo pone sobre el menú compacto y los desplegables. */}
    {textosHub && (
      <ConfirmDialog
        open={confirmandoHub}
        title={textosHub.titulo}
        message={textosHub.mensaje}
        confirmLabel={textosHub.confirmar}
        cancelLabel={textosHub.cancelar}
        loading={yendoAlHub}
        zIndexClass="z-[60]"
        onConfirm={irAlHub}
        onCancel={() => setConfirmandoHub(false)}
      />
    )}
    </>
  );
}
